import { useCallback, useState } from 'react'
import { BoxProps, Icon } from '@chakra-ui/react'
import { DropzoneOptions } from 'react-dropzone'
import { Box, ButtonOutlined, Flex, Text } from 'components'
import { Label } from 'components/core/Label'
import { UploadArea } from 'components/UploadArea'
import { DocumentIcon } from 'components/icons'
import { useNotify, useTranslations } from 'hooks'
import translations from './DocumentUpload.i18n.json'

type Props = BoxProps & {
  label?: string
  placeholder?: string
  options?: DropzoneOptions
  onDataURL?: (dataURL: string, file: File) => void
  onClear?: () => void
}

export function DocumentUpload({ label, placeholder, options, onDataURL, onClear, ...boxProps }: Props) {
  const t = useTranslations(translations)
  const notify = useNotify()
  const [selectedFile, setSelectedFile] = useState<File>()

  const handleDropRejected = useCallback(
    ([file]) => {
      const messages = file.errors.map((error: any) => error.message).join()
      notify.error(messages || t.invalidFile)
    },
    [notify, t]
  )

  const handleDropAccepted = useCallback(
    ([file]: File[]) => {
      const reader = new FileReader()
      reader.onload = () => {
        setSelectedFile(file)
        onDataURL?.(reader.result as string, file)
      }
      reader.onerror = () => notify.error(t.fileReadError)
      reader.readAsDataURL(file)
    },
    [onDataURL, notify, t]
  )

  const handleChangeClick = useCallback(() => {
    setSelectedFile(undefined)
    onClear?.()
  }, [onClear])

  return (
    <Box {...boxProps}>
      {label && (
        <Label fontSize="xs" shade={1} mb={1}>
          {label}
        </Label>
      )}
      {selectedFile ? (
        <Flex align="center" justify="space-between" border="1px dashed" borderColor="light.0" p={4}>
          <Flex align="center" minWidth={0}>
            <Box shade={3} mr={2} lineHeight={0}>
              <Icon as={DocumentIcon} boxSize="20px" />
            </Box>
            <Text fontSize="sm" isTruncated>
              {selectedFile.name}
            </Text>
          </Flex>
          <ButtonOutlined onClick={handleChangeClick} size="sm" px={4} ml={3} flexShrink={0}>
            {t.change}
          </ButtonOutlined>
        </Flex>
      ) : (
        <UploadArea
          icon={DocumentIcon}
          multiple={false}
          placeholder={placeholder ?? t.dragAndDropADocumentOr}
          onDropAccepted={handleDropAccepted}
          onDropRejected={handleDropRejected}
          {...options}
        />
      )}
    </Box>
  )
}
